import React, {useCallback, useMemo} from 'react';
import {useJobContext} from 'contexts/JobContext';
import {useAppContext} from 'contexts/ApplicationContext';
import {PromptButton} from 'shared/PromptButton';
import {PartialJob} from 'shared/types/PartialJob';
import {useUnsavedChangesDialog} from 'shared/components/ConfirmationDialog/UnsavedChangesDialog';
import {useJobSubmitHandler} from 'components/customer/Job/helpers/useJobSubmitHandler';
import {Loader} from 'shared/helpers';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {clearChanges} from 'store/customer/appSlice';
import {getIsSavingProduct} from 'components/customer/Preview3DCommon/store/viewerSlice';
import {shallowEqual} from 'react-redux';
import styled from 'styled-components';
import useUI from 'shared/useUI';
import {submitJobButtonText} from 'shared/components/Header/helper/submitJobButtonText';
import {useJobVariations} from 'shared/components/Header/helper/useJobVariations';
import {usePaymentSettings} from 'components/customer/Confirmation/helpers/usePaymentSettings';
import {useIsTouchDevices} from 'shared/helpers/DeviceSize';
import {useGetJobCostQuery} from 'components/customer/Job/store/jobApi';
import {useIsConfirmationPage} from 'components/customer/Confirmation/helpers/useIsConfirmationPage';

interface SubmitButtonProps {
    left?: boolean;
    showLabel?: boolean;
}

export const SubmitButton = ({
    left = false,
    showLabel = true,
}: SubmitButtonProps) => {
    const dispatch = useAppDispatch();
    const isSavingProduct = useAppSelector(getIsSavingProduct, shallowEqual);
    const {job, isPageDirty, expiredJobPrice, isRoomPlanner} =
        useJobContext() as PartialJob;
    const {handleJobSubmission, loader} = useJobSubmitHandler();
    const {noVariations, notRemovableExtraVariatons, extraVariation} =
        useJobVariations();
    const {userProfile} = useAppContext();
    const {isConfirmationPage} = useIsConfirmationPage();
    const captureDeliveryInfoAtConfirmation =
        !!userProfile?.jobConfirmationEnabled &&
        !userProfile?.capturePickupFreightAtJobCreation;
    const {isSmallScreenLayout} = useUI();
    const {paymentSettings} = usePaymentSettings();
    const isTouchDevice = useIsTouchDevices();
    const {data: jobCost, isFetching: isLoadingJobCost} = useGetJobCostQuery(
        {jobId: Number(job?.id)},
        {skip: !job?.id}
    );

    const {dialog, showDialog} = useUnsavedChangesDialog();

    const handleSubmitClick = useCallback(
        (e: React.MouseEvent) => {
            e.persist();

            if (isPageDirty) {
                showDialog({
                    onConfirmDiscard: () => {
                        // Clear pending changes before submitting: job
                        // submission navigates away, and the landing page's
                        // NavigationConfirmation blocks on the same redux
                        // "changes" state, which would otherwise pop a
                        // second unsaved-changes dialog right after this one.
                        dispatch(clearChanges());

                        return handleJobSubmission(e);
                    },
                });
            } else {
                void handleJobSubmission(e);
            }
        },
        [isPageDirty, showDialog, handleJobSubmission, dispatch]
    );

    const submitButtonText = useMemo(
        () =>
            submitJobButtonText({
                job,
                jobConfirmationEnabled: userProfile?.jobConfirmationEnabled,
                noVariations,
                extraVariation,
                paymentGatewaysEnabled:
                    userProfile?.jobConfirmationEnabled &&
                    (paymentSettings?.allowPayOnAccount ||
                        paymentSettings?.enableCardPayments ||
                        paymentSettings?.enableInvoicePayments),
            }),
        [
            job.status,
            noVariations,
            notRemovableExtraVariatons,
            extraVariation,
            job.freightCost,
            userProfile,
            paymentSettings,
        ]
    );

    return (
        <>
            <Loader loader={!isRoomPlanner && (loader || isSavingProduct)} />
            {userProfile?.inActiveManufacturer == 0 &&
            [0, 11, 19, 5].includes(job.status) &&
            !expiredJobPrice ? (
                <>
                    <StyledPromptButton
                        $left={left}
                        $isSmallDevice={!isTouchDevice && isSmallScreenLayout}
                        disabled={
                            Object.keys(job).length == 0 ||
                            job.totalProductCount == 0 ||
                            isLoadingJobCost ||
                            ((isConfirmationPage ||
                                !captureDeliveryInfoAtConfirmation) &&
                                !!jobCost?.missingRequiredDeliveryExtraGroups
                                    ?.length)
                        }
                        iconName={left ? 'Submit.svg' : 'Button-Submit.svg'}
                        action={handleSubmitClick}
                        showConfirmation={false}
                        className={
                            left ? 'submit-button-left' : 'button-blue btn-sm'
                        }
                        title={left ? submitButtonText : null}>
                        {showLabel ? submitButtonText : ''}
                    </StyledPromptButton>
                    {dialog}
                </>
            ) : null}
        </>
    );
};

const StyledPromptButton = styled((props) => <PromptButton {...props} />)<{
    $isSmallDevice?: boolean;
    $left?: boolean;
}>`
    ${({$left}) =>
        $left
            ? `
        &,
        &:hover,
        &:focus,
        &:active {
            text-decoration: none !important;
            color: inherit !important;
            transition: none !important;
        }
    `
            : ''}

    > div:first-child {
        ${({$isSmallDevice, $left}) =>
            $isSmallDevice && $left ? 'width: 45px !important;' : ''}
    }
    > div > img {
        margin: 0 auto !important;
    }

    > span {
        ${({$left}) => ($left ? 'text-align: left;' : '')}
    }
`;
